import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectOption } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { formatCurrency, isMobile, handleNumberInput } from '../../lib/utils';

const OtherExpensesStep = ({ formData, setFormData }) => {
  const [newExpense, setNewExpense] = useState({
    name: '',
    amount: 0,
    description: '',
    date: '',
    paid: false,
    channel: 'bank_transfer'
  });

  const addOtherExpense = () => {
    if (!newExpense.name || !newExpense.amount) {
      return;
    }

    const expense = {
      ...newExpense,
      id: Date.now()
    };

    setFormData(prev => ({
      ...prev,
      other_expenses: [...(prev.other_expenses || []), expense]
    }));

    setNewExpense({
      name: '',
      amount: 0,
      description: '',
      date: '',
      paid: false,
      channel: 'bank_transfer'
    });
  };

  const removeOtherExpense = (id) => {
    setFormData(prev => ({
      ...prev,
      other_expenses: (prev.other_expenses || []).filter(expense => expense.id !== id)
    }));
  };

  const updateOtherExpense = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      other_expenses: (prev.other_expenses || []).map(expense =>
        expense.id === id ? { ...expense, [field]: value } : expense
      )
    }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-3">
          <FileText className="h-6 w-6 text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">ค่าใช้จ่ายโปรเจค</h2>
        <p className="text-muted-foreground text-sm">จัดการค่าใช้จ่ายโปรเจค</p>
      </div>

      {/* Other Expenses */}
      <Card className="border-2 border-red-200 hover:border-red-300 transition-colors">
        <CardHeader className="bg-red-50 py-3">
          <CardTitle className="flex items-center gap-2 text-red-800">
            <FileText className="h-5 w-5" />
            ค่าใช้จ่ายอื่นๆ
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* Add New Expense */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="expense_name">ชื่อค่าใช้จ่าย</Label>
              <Input
                id="expense_name"
                placeholder="เช่น ค่าโฆษณา, ค่าอุปกรณ์"
                value={newExpense.name}
                onChange={(e) => setNewExpense(prev => ({ ...prev, name: e.target.value }))}
                className="h-10"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expense_amount">จำนวนเงิน</Label>
              <Input
                id="expense_amount"
                type={isMobile() ? "number" : "text"}
                placeholder="10000"
                value={newExpense.amount}
                onChange={(e) => setNewExpense(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                onKeyDown={(e) => !isMobile() && handleNumberInput(e, (e) => setNewExpense(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 })))}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expense_date">วันที่</Label>
              <Input
                id="expense_date"
                type="date"
                value={newExpense.date}
                onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                className="h-10"
              />
            </div>
          </div>

          <div className="mb-6">
            <Button
              onClick={addOtherExpense}
              disabled={!newExpense.name || !newExpense.amount}
              className="w-full h-10"
            >
              <Plus className="h-4 w-4 mr-2" />
              เพิ่มค่าใช้จ่าย
            </Button>
          </div>

          {/* Expenses Table */}
          {formData.other_expenses && formData.other_expenses.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">ชื่อค่าใช้จ่าย</TableHead>
                    <TableHead className="w-24 text-right">จำนวนเงิน</TableHead>
                    <TableHead className="w-32">รายละเอียด</TableHead>
                    <TableHead className="w-28">วันที่</TableHead>
                    <TableHead className="w-20 text-center">สถานะ</TableHead>
                    <TableHead className="w-20 text-center">ช่องทาง</TableHead>
                    <TableHead className="w-16 text-center">จัดการ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(formData.other_expenses || []).map((expense, index) => (
                    <TableRow key={expense.id || index}>
                      <TableCell className="font-medium text-xs">
                        {expense.name}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="รายละเอียด"
                          value={expense.description || ''}
                          onChange={(e) => updateOtherExpense(expense.id, 'description', e.target.value)}
                          className="h-7 text-xs w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={expense.date || ''}
                          onChange={(e) => updateOtherExpense(expense.id, 'date', e.target.value)}
                          className="h-7 text-xs w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={expense.paid ? 'paid' : 'pending'}
                          onChange={(e) => updateOtherExpense(expense.id, 'paid', e.target.value === 'paid')}
                          className="text-xs"
                        >
                          <SelectOption value="pending">รอชำระ</SelectOption>
                          <SelectOption value="paid">ชำระแล้ว</SelectOption>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={expense.channel || 'bank_transfer'}
                          onChange={(e) => updateOtherExpense(expense.id, 'channel', e.target.value)}
                          className="text-xs"
                        >
                          <SelectOption value="bank_transfer">โอนเงิน</SelectOption>
                          <SelectOption value="cash">เงินสด</SelectOption>
                          <SelectOption value="check">เช็ค</SelectOption>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removeOtherExpense(expense.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Summary Row */}
                  {formData.other_expenses && formData.other_expenses.length > 0 && (
                    <TableRow className="bg-gray-50 border-t-2 border-gray-200">
                      <TableCell className="text-center font-semibold text-gray-800">
                        รวม
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-800">
                        {formatCurrency(
                          (formData.other_expenses || []).reduce((sum, expense) => sum + (expense.amount || 0), 0)
                        )}
                      </TableCell>
                      <TableCell colSpan="2" className="text-center text-xs text-gray-600">
                        {(formData.other_expenses || []).length} รายการ
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-xs text-gray-600">
                          {(formData.other_expenses || []).filter(expense => expense.paid).length} / {(formData.other_expenses || []).length}
                        </div>
                      </TableCell>
                      <TableCell colSpan="2" className="text-center text-xs text-gray-600">
                        {(formData.other_expenses || []).length} รายการ
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">ยังไม่มีค่าใช้จ่ายอื่นๆ</p>
              <p className="text-sm text-muted-foreground">กรุณาเพิ่มค่าใช้จ่ายใหม่</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OtherExpensesStep;
